import React, { useContext, useEffect, useState } from 'react';

import { navigate } from '@reach/router';
import RichTextEditor, { EditorValue } from 'react-rte';

import { GlobalContext } from '../../../../../store';
import { IAdminEvent } from '../../../../../store/admin/events/store';

import { FormConsumer, FormProvider, IFormAttributes } from '../../../../../components/Forms';
import { lazyEqual } from '../../../../../contrib/utils';
import { EventWizardStepTitle } from '../../Components/EventWizardStepTitle/EventWizardStepTitle';
import { EventBasicsSection } from '../EventBasicsSection';
import { HandicapsSection } from './HandicapsSection';
import { EventInformationSection } from './EventInformationSection';
import { useGetAdminEvent } from '../../../../../contrib/Hooks/useGetAdminEvent';

interface IProps {
  eventId: string;
  clubId: string;
}

const exit = (clubId: string) => navigate(`/staff/club/${clubId}`);

const forward = (clubId: string, eventId: string) =>
  navigate(`/staff/club/${clubId}/event/${eventId}/setup/formats`);

export const EventBasics = (props: IProps) => {
  const { clubId, eventId } = props;

  const { actions, adminEvents } = useContext(GlobalContext);

  const [handicapMetricStatus, setHandicapMetricStatus] = useState(false);
  const [handicapMetricStatusTouched, setHandicapMetricStatusTouched] = useState(false);

  const event = useGetAdminEvent(eventId);

  const [text, setText] = useState<EditorValue>(RichTextEditor.createEmptyValue());

  useEffect(() => {
    if (event) {
      setText(
        event.description
          ? RichTextEditor.createValueFromString(event.description, 'markdown')
          : RichTextEditor.createEmptyValue()
      );

      if (!handicapMetricStatusTouched) {
        setHandicapMetricStatus(!!event.handicapMetric);
      }
    }
  }, [event]);

  const onChangeToggle = (type: string, setValue: any) => async (
    _: any,
    value: boolean
  ): Promise<void> => {
    if (type === 'handicap') {
      setHandicapMetricStatus(!handicapMetricStatus);
      if (!handicapMetricStatusTouched) {
        setHandicapMetricStatusTouched(true);
      }
    }
    setValue(value);
  };

  const save = async (newEvent: IAdminEvent, noAdvance?: boolean) => {
    if (lazyEqual(event, newEvent)) {
      if (noAdvance) {
        exit(clubId);
      } else {
        forward(clubId, eventId);
      }
      return;
    }

    const response = await actions.adminEvents.edit(Number(clubId), newEvent);

    const imageUpdateResponse =
      (newEvent as IAdminEvent).image &&
      (newEvent as IAdminEvent).image !== adminEvents[Number(eventId)].image
        ? await actions.adminEvents.editImage(Number(clubId), newEvent as IAdminEvent)
        : undefined;

    if (response.adminEvents && response.adminEvents.current) {
      const current = response.adminEvents.current;
      if (current.rounds.length) {
        for (const round of current.rounds) {
          actions.adminRoundFormats.get(Number(clubId), current.id, round.id);
        }
      }
    }
    if (!response.errors && !noAdvance && (!imageUpdateResponse || !imageUpdateResponse.errors)) {
      // TODO: Fix errors and border condition
      if (!noAdvance) {
        forward(clubId, eventId);
      } else {
        exit(clubId);
      }
    } else if (response.errors) {
      await actions.notifications.push({
        status: 'unread',
        text: response.errors.events.msg || '',
        type: 'error'
      });
    }
  };

  return event ? (
    <FormProvider
      defaultValues={{
        ...event,
        totalPlayers: event.totalPlayers || 1,
        totalRounds: event.totalRounds || 1
      }}
      onSubmit={save}
      validations={{
        totalPlayers: 'positive',
        totalRounds: 'positive'
      }}
    >
      <FormConsumer>
        {({ values, formActions: { setValue }, errors }: IFormAttributes) => (
          <div className="event-wizard-page form event-basics">
            <EventWizardStepTitle
              buttonText="SAVE & CONTINUE"
              buttonDisabled={errors.FORM}
              title="EVENT SETUP"
            />

            <EventBasicsSection errors={errors} values={values} setValue={setValue} />

            <HandicapsSection
              handicapMetricStatus={handicapMetricStatus}
              values={values}
              setValue={setValue}
              onChangeToggle={onChangeToggle}
            />

            <EventInformationSection
              eventId={eventId}
              text={text}
              setText={setText}
              setValue={setValue}
            />
          </div>
        )}
      </FormConsumer>
    </FormProvider>
  ) : (
    <></>
  );
};
